import React, { useEffect, useMemo, useState } from 'react';
import { collection } from 'firebase/firestore';
import EditButton from '../../../ui/EditButton';
import { db } from '../../../../firebase-client';
import { safeOnSnapshot } from '../../../../utils/firestoreSubscriptions';
import {
  calculateLearnToSailFinalSessionEnd,
  getLearnToSailGeneralSettings,
  isLearnToSailCoursePubliclyActionable,
  normalizeLearnToSailSessions,
  normalizeLearnToSailSignupMode,
} from '../../utils/learnToSail';

function asDate(value) {
  if (value?.toDate) return value.toDate();
  if (value instanceof Date) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
}

function formatDateRange(sessions = []) {
  const normalized = normalizeLearnToSailSessions(sessions);
  if (normalized.length === 0) return 'No sessions yet';
  const first = new Date(`${normalized[0].date}T${normalized[0].startTime}`);
  const last = new Date(`${normalized[normalized.length - 1].date}T${normalized[normalized.length - 1].endTime}`);
  if (Number.isNaN(first.getTime()) || Number.isNaN(last.getTime())) return 'Dates incomplete';
  const firstLabel = first.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  const lastLabel = last.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  return firstLabel === lastLabel ? firstLabel : `${firstLabel} - ${lastLabel}`;
}

function sortCourses(left, right) {
  const leftEnd = calculateLearnToSailFinalSessionEnd(left?.sessions || [])?.getTime() || 0;
  const rightEnd = calculateLearnToSailFinalSessionEnd(right?.sessions || [])?.getTime() || 0;
  if (leftEnd !== rightEnd) return leftEnd - rightEnd;
  const leftUpdated = asDate(left?.updatedAt)?.getTime() || 0;
  const rightUpdated = asDate(right?.updatedAt)?.getTime() || 0;
  return rightUpdated - leftUpdated;
}

export default function LearnToSailCard({
  isAdmin,
  teamId,
  modules = {},
  onManage,
  title = 'Learn to Sail',
}) {
  const enabled = Boolean(modules?.sailing?.learnToSail?.enabled);
  const generalSettings = getLearnToSailGeneralSettings(modules?.sailing?.learnToSail || {});
  const waitlistOpen = generalSettings.waitlistOpen;
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    if (!isAdmin || !enabled || !teamId) {
      setCourses([]);
      return undefined;
    }
    const unsubscribe = safeOnSnapshot(
      collection(db, 'teams', teamId, 'learnToSailCourses'),
      (snapshot) => {
        const rows = snapshot.docs
          .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .sort(sortCourses);
        setCourses(rows);
      },
      undefined,
      { label: 'learn-to-sail-courses', teamId }
    );
    return () => unsubscribe();
  }, [enabled, isAdmin, teamId]);

  const summary = useMemo(
    () => courses.reduce((acc, course) => {
      const finalSessionEnd = calculateLearnToSailFinalSessionEnd(course?.sessions || []);
      if (finalSessionEnd && finalSessionEnd.getTime() >= Date.now()) acc.upcoming += 1;
      else acc.completed += 1;
      acc.places += Math.max(0, Number(course?.capacity || 0));
      return acc;
    }, { upcoming: 0, completed: 0, places: 0 }),
    [courses]
  );

  if (!isAdmin || !enabled) return null;

  return (
    <div className="team-card">
      <div className="card-header-with-icon">
        <span className="card-title">{title}</span>
        <div style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
          <button
            type="button"
            className="btn btn--secondary btn--xs"
            onClick={() => onManage?.({ createNew: true })}
          >
            New course
          </button>
          <EditButton section="learnToSail" onEdit={() => onManage?.({})} />
        </div>
      </div>

      <div className="card-body">
        <div className="availability-status-list">
          <div className="availability-status-item">
            <div className="availability-status-label">
              <span className="availability-status-icon">🚣</span>
              <span>Upcoming</span>
            </div>
            <span className="availability-pill available">{summary.upcoming}</span>
          </div>

          <div className="availability-status-item">
            <div className="availability-status-label">
              <span className="availability-status-icon">✅</span>
              <span>Completed</span>
            </div>
            <span className="availability-pill unavailable">{summary.completed}</span>
          </div>

          <div className="availability-status-item">
            <div className="availability-status-label">
              <span className="availability-status-icon">🎟️</span>
              <span>Total places</span>
            </div>
            <span className="availability-pill available">{summary.places}</span>
          </div>

          <div className="availability-status-item">
            <div className="availability-status-label">
              <span className="availability-status-icon">🧾</span>
              <span>Generic waitlist</span>
            </div>
            <span className={`availability-pill ${waitlistOpen ? 'available' : 'unavailable'}`}>{waitlistOpen ? 'Open' : 'Closed'}</span>
          </div>

          <div className="availability-status-item">
            <div className="availability-status-label">
              <span className="availability-status-icon">💸</span>
              <span>Fee handling</span>
            </div>
            <span className="availability-pill available">
              MN {generalSettings.memberNestFeeHandling === 'add' ? 'add' : 'deduct'} · Stripe {generalSettings.stripeFeeHandling === 'add' ? 'add' : 'deduct'}
            </span>
          </div>
        </div>

        {courses.length === 0 ? (
          <p className="text-muted" style={{ marginTop: 12 }}>
            No Learn to Sail courses yet. Add a course to publish signup, calendar sessions, capacity, and conversion settings.
          </p>
        ) : (
          <div style={{ marginTop: 12, display: 'grid', gap: 8, maxHeight: '20rem', overflowY: 'auto', paddingRight: 4 }}>
            {courses.map((course) => {
              const finalSessionEnd = calculateLearnToSailFinalSessionEnd(course?.sessions || []);
              const isPast = finalSessionEnd instanceof Date && finalSessionEnd.getTime() < Date.now();
              const signupMode = normalizeLearnToSailSignupMode(course);
              const courseActionable = isLearnToSailCoursePubliclyActionable(course, waitlistOpen);
              const invalidWaitlistCourse = signupMode === 'waitlist_only' && !waitlistOpen;
              const signupModeLabel = signupMode === 'open_to_all'
                ? 'Open to all'
                : signupMode === 'waitlist_only'
                  ? (waitlistOpen ? 'Admin release via waitlist' : 'Needs setup: open waitlist')
                  : 'Closed';
              return (
                <button
                  key={course.id}
                  type="button"
                  className="btn btn--secondary"
                  style={{ justifyContent: 'space-between', display: 'flex', alignItems: 'center', textAlign: 'left', gap: 12 }}
                  onClick={() => onManage?.({ focusCourseId: course.id })}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600 }}>{course?.name || 'Untitled course'}</div>
                    <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                      {formatDateRange(course?.sessions || [])} · {normalizeLearnToSailSessions(course?.sessions || []).length} session{normalizeLearnToSailSessions(course?.sessions || []).length === 1 ? '' : 's'}
                    </div>
                    <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                      Capacity {Number(course?.capacity || 0)} · {signupModeLabel} · Purge after {Math.max(1, Number(course?.postCoursePurgeDays || 1))} day{Math.max(1, Number(course?.postCoursePurgeDays || 1)) === 1 ? '' : 's'}
                    </div>
                  </div>
                  <span className={`availability-pill ${isPast || !courseActionable ? 'unavailable' : 'available'}`}>
                    {isPast ? 'Completed' : invalidWaitlistCourse ? 'Setup needed' : signupMode === 'closed' ? 'Closed' : 'Live'}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
